/**
 * The screen shown while the interpretive report is generated, when it is ready to open, and
 * when generation failed.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Label, Paragraph, Spinner } from '@digdir/designsystemet-react';
import { ArrowLeftIcon, BellIcon, CheckmarkIcon } from '@navikt/aksel-icons';
import {
  clampPercent,
  minutesLeft,
  phasesView,
  currentPhaseIndex,
  generationScreenOf,
  GENERATION_PHASES,
  type GenerationScreen,
  type PhaseState,
} from './generationProgressView';
import styles from './InterpretiveReportGenerationProgress.module.css';

type Props = {
  progress: number;
  step: string | null;
  startedAt: string | null;
  failed: boolean;
  ready: boolean;
  onBack: () => void;
  onOpen: () => void;
  onRetry: () => void;
};

const HEADING_KEY: Record<GenerationScreen, string> = {
  working: 'interpretiveReport.progress.heading',
  ready: 'interpretiveReport.progress.readyHeading',
  failed: 'interpretiveReport.progress.failedHeading',
};

/** The estimate follows the clock between status updates */
const CLOCK_TICK_MS = 15_000;

const useNow = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
};

const PhaseMarker = ({ state }: { state: PhaseState }) => {
  if (state === 'done') {
    return (
      <span className={styles.done} aria-hidden>
        <CheckmarkIcon />
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span className={styles.marker} aria-hidden>
        <Spinner aria-label="" data-size="sm" />
      </span>
    );
  }
  return <span className={styles.todo} aria-hidden />;
};

export const InterpretiveReportGenerationProgress = ({
  progress,
  step,
  startedAt,
  failed,
  ready,
  onBack,
  onOpen,
  onRetry,
}: Props) => {
  const { t } = useTranslation();
  const now = useNow();
  const percent = ready ? 100 : clampPercent(progress);
  const minutes = ready ? null : minutesLeft(percent, startedAt, now);
  const screen = generationScreenOf({ failed, ready });
  const textOf: Record<GenerationScreen, () => string> = {
    working: () => `${t(`interpretiveReport.progress.phases.${GENERATION_PHASES[currentPhaseIndex(step)]}`)}…`,
    ready: () => t('interpretiveReport.progress.readyText'),
    failed: () => t('interpretiveReport.progress.failedText'),
  };

  return (
    <div className={styles.page}>
      <div className={styles.column}>
        <div>
          <Button variant="tertiary" data-size="sm" onClick={onBack}>
            <ArrowLeftIcon aria-hidden />
            {t('common.back')}
          </Button>
        </div>

        <div className={styles.card}>
          <div className={styles.intro}>
            <Label data-size="sm" className={styles.eyebrow}>
              {t('interpretiveReport.document.header')}
            </Label>
            <Heading level={1} data-size="md" aria-live="polite">
              {t(HEADING_KEY[screen])}
            </Heading>
            <Paragraph className={styles.subtle}>{textOf[screen]()}</Paragraph>
          </div>

          {screen === 'failed' ? (
            <Button variant="secondary" className={styles.action} onClick={onRetry}>
              {t('interpretiveReport.tryAgain')}
            </Button>
          ) : (
            <>
              <div className={styles.progress}>
                <div
                  role="progressbar"
                  aria-label={t('interpretiveReport.progress.label')}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  className={styles.track}
                >
                  <div className={styles.fill} style={{ width: `${percent}%` }} />
                </div>
                <div className={styles.progressText}>
                  <Label data-size="sm" className={styles.percent}>
                    {ready
                      ? t('interpretiveReport.progress.finished')
                      : t('interpretiveReport.progress.percent', { percent })}
                  </Label>
                  {minutes !== null && (
                    <Paragraph data-size="sm" className={styles.subtle}>
                      {t('interpretiveReport.progress.minutesLeft', { count: minutes })}
                    </Paragraph>
                  )}
                </div>
              </div>

              <ol className={styles.phases}>
                {phasesView(step, ready).map(({ phase, state }) => (
                  <li key={phase} className={styles.phase} data-state={state}>
                    <PhaseMarker state={state} />
                    <Paragraph className={styles.phaseLabel}>
                      {t(`interpretiveReport.progress.phases.${phase}`)}
                      {state !== 'todo' && (
                        <span className="ds-sr-only">
                          {t(`interpretiveReport.progress.phaseState.${state}`)}
                        </span>
                      )}
                    </Paragraph>
                  </li>
                ))}
              </ol>

              {!ready && (
                <div className={styles.note}>
                  <BellIcon aria-hidden className={styles.noteIcon} />
                  <Paragraph data-size="sm">{t('interpretiveReport.progress.background')}</Paragraph>
                </div>
              )}

              {ready && (
                <Button variant="primary" className={styles.action} onClick={onOpen}>
                  {t('interpretiveReport.progress.open')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
